import { useEffect, useState, useRef, useCallback } from "react";
import { Calendar } from "react-date-range";
import clsx from "clsx";

import { getDatePickerButton } from "@utils/index";
import { useClickOutside } from "@hooks/index";
import { CalendarMonthSvg, FormInput } from "@components/index";

const formatInputDate = (date: string) =>
  date
    ? new Date(
        `${date.split(".")[2]}-${parseInt(date.split(".")[1], 10)}-${date.split(".")[0]}`.replace(
          /-/g,
          "/",
        ),
      )
    : new Date();

const formatStateDate = (date: Date) => {
  if (!date) {
    return "";
  }

  let day = String(date.getDate());
  day = day.length === 1 ? `0${day}` : day;
  let month = String(date.getMonth() + 1);
  month = month.length === 1 ? `0${month}` : month;

  return `${day}.${month}.${date.getFullYear()}`;
};

const formatEnteredDate = (date: string) => {
  const justNumbers = date.replace(/[^0-9]/g, "");
  let str = justNumbers.slice(0, 2);
  str += justNumbers.length > 2 ? "." : "";
  str += justNumbers.slice(2, 4);
  str += justNumbers.length > 4 ? "." : "";
  str += justNumbers.slice(4, 8);

  return str;
};

export const DatePicker = ({
  parent = "",
  disabled,
  date: defaultDate,
  prefix = "",
  shouldCloseOnClickOutside = false,
  label = "Date",
  error,
  dataTestid,
  onSetDate,
}: {
  parent?: string;
  date?: Date;
  prefix?: string;
  disabled?: boolean;
  shouldCloseOnClickOutside?: boolean;
  label?: string;
  error?: string;
  dataTestid?: string;
  onSetDate: (date?: Date) => void;
}) => {
  const [date, setDate] = useState<Date | undefined>(defaultDate);
  const [isShow, setIsShow] = useState(false);

  const datePickerRef = useRef(null);
  useClickOutside(
    datePickerRef,
    () => shouldCloseOnClickOutside && setIsShow(false),
  );

  const [dateInputElement, setDateInputElement] = useState<HTMLInputElement>();

  const handleInput = useCallback(
    ({ currentTarget }: Event) => {
      if (currentTarget) {
        const invalidDateSection = document.querySelector(
          `${parent ? parent + " " : ""}.rdrInvalidDateSection`,
        );
        invalidDateSection?.classList.add("hidden");
        const value = (currentTarget as HTMLInputElement).value;
        const formattedValue = formatEnteredDate(value);
        (currentTarget as HTMLInputElement).value = formattedValue;
        const convertedToDate = formatInputDate(value);
        if (
          value.length < 10 ||
          convertedToDate.toString() === "Invalid Date"
        ) {
          invalidDateSection?.classList.remove("hidden");
        } else {
          setDate(convertedToDate);
        }
      }
    },
    [parent],
  );

  useEffect(() => {
    if (
      !document.querySelector(
        `${parent ? parent + " " : ""}.rdrCalendarWrapper .rdrDatePickerFooter`,
      )
    ) {
      const btnCancel = getDatePickerButton("Cancel", "cancel");
      const btnApply = getDatePickerButton("Apply", "apply", "apply");
      const footer = document.createElement("div");
      footer.className = "rdrDatePickerFooter";
      footer.appendChild(btnCancel);
      footer.appendChild(btnApply);
      const pickerBottom = document.querySelector(
        `${parent ? parent + " " : ""}.rdrCalendarWrapper`,
      );
      pickerBottom?.appendChild(footer);
    }
  }, [onSetDate, parent, defaultDate]);

  useEffect(() => {
    if (
      !document.querySelector(
        `${parent ? parent + " " : ""}.rdrCalendarWrapper .rdrDateDisplayWrapper .rdrInputSection`,
      )
    ) {
      const inputDate = document.createElement("input");
      inputDate.name = "date";
      inputDate.maxLength = 10;
      inputDate.placeholder = "Date";
      inputDate.className =
        "w-[142px] md:w-[310px] h-[48px] px-4 border border-black-16 hover:border-black-32 focus:border-black-32 rounded-lg text-200 text-black-100 placeholder:text-black-54 outline-none";
      const inputSection = document.createElement("div");
      inputSection.className = "rdrInputSection flex justify-between px-4 pt-2";
      inputSection.appendChild(inputDate);
      const pickerTop = document.querySelector(
        `${parent ? parent + " " : ""}.rdrCalendarWrapper .rdrDateDisplayWrapper`,
      );
      pickerTop?.appendChild(inputSection);

      const invalidDateSection = document.createElement("div");
      invalidDateSection.className =
        "rdrInvalidDateSection text-center text-red-100 hidden";
      invalidDateSection.innerText = "You entered an invalid date";
      pickerTop?.appendChild(invalidDateSection);

      inputDate.onblur = handleInput;
      setDateInputElement(inputDate);
    }
  }, [handleInput, parent]);

  useEffect(() => {
    const btn: HTMLButtonElement | null = document.querySelector(
      `${parent ? parent + " " : ""}.rdrCalendarWrapper .rdrDatePickerFooter button.cancel`,
    );
    if (btn) {
      btn.onclick = () => {
        setDate(defaultDate);
        onSetDate(defaultDate);
        setIsShow(false);
        const invalidDateSection = document.querySelector(
          `${parent ? parent + " " : ""}.rdrInvalidDateSection`,
        );
        if (
          invalidDateSection &&
          !invalidDateSection.classList.contains("hidden")
        ) {
          return;
        }

        setIsShow(false);
      };
    }
  }, [defaultDate, onSetDate, parent]);

  useEffect(() => {
    const btn: HTMLButtonElement | null = document.querySelector(
      `${parent ? parent + " " : ""}.rdrCalendarWrapper .rdrDatePickerFooter button.apply`,
    );
    if (btn) {
      btn.onclick = () => {
        const invalidDateSection = document.querySelector(
          `${parent ? parent + " " : ""}.rdrInvalidDateSection`,
        );
        if (
          invalidDateSection &&
          !invalidDateSection.classList.contains("hidden")
        ) {
          return;
        }

        if (date) {
          const dateMonth = date.getMonth() + 1;
          const dateMonthStr =
            dateMonth < 10 ? `0${dateMonth}` : `${dateMonth}`;
          const dateDay = date.getDate();
          const dateDayStr = dateDay < 10 ? `0${dateDay}` : `${dateDay}`;
          onSetDate(
            formatInputDate(
              `${date.getFullYear()}-${dateMonthStr}-${dateDayStr}`,
            ),
          );
        }
        setIsShow(false);
      };
    }
  }, [date, onSetDate, parent]);

  useEffect(() => {
    if (dateInputElement && date) {
      dateInputElement.value = formatStateDate(date);
    }
  }, [dateInputElement, date]);

  useEffect(() => {
    setDate(defaultDate);
  }, [defaultDate]);

  return (
    <div
      ref={datePickerRef}
      className={clsx("relative", disabled && "pointer-events-none opacity-50")}
      data-testid={dataTestid}
    >
      <FormInput
        name={`${prefix ? `${prefix}__` : ""}date`}
        label={label}
        icon={<CalendarMonthSvg />}
        value={!defaultDate ? "" : formatStateDate(defaultDate)}
        error={error}
        onChange={() => setDate(date)}
        onClick={() => setIsShow(true)}
        readOnly={isShow}
      />
      <Calendar
        className={clsx(
          "before:content-[' '] absolute right-10 z-10 mt-3 rounded-lg border border-lightGray shadow-md before:absolute before:-top-1 before:right-4 before:z-0 before:block before:h-3 before:w-3 before:rotate-45 before:bg-white-100",
          !isShow && "!hidden",
        )}
        date={date}
        dateDisplayFormat="d MMMM yyyy"
        weekStartsOn={1}
        onChange={(value: Date) => {
          setDate(value);
          const invalidDateSection = document.querySelector(
            `${parent ? parent + " " : ""}.rdrInvalidDateSection`,
          );
          if (
            invalidDateSection &&
            !invalidDateSection.classList.contains("hidden")
          ) {
            invalidDateSection?.classList.add("hidden");
          }
        }}
      />
    </div>
  );
};
